import React, { useState, useEffect } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';

const DraggableLocationList = ({ availableLocations, selectedLocations, onLocationOrderChange }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);
  const [unselectedLocations, setUnselectedLocations] = useState([]);

  useEffect(() => {
    const unselected = availableLocations.filter(location => 
      !selectedLocations.includes(location)
    );
    setUnselectedLocations(unselected);
  }, [availableLocations, selectedLocations]);

  const handleDragStart = (e, location, source) => {
    setDraggedItem({ location, source });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index, source) => {
    e.preventDefault();
    setDraggedOver({ index, source });
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggedOver(null);
    }
  };

  const handleDrop = (e, dropIndex, targetSource) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    const { location, source } = draggedItem;
    
    if (targetSource === 'selected') {
      let newSelected = [...selectedLocations];
      
      if (source === 'unselected') {
        newSelected.splice(dropIndex, 0, location);
      } else if (source === 'selected') {
        const currentIndex = selectedLocations.indexOf(location);
        if (currentIndex !== -1) {
          newSelected.splice(currentIndex, 1);
          newSelected.splice(dropIndex, 0, location);
        }
      }
      
      onLocationOrderChange(newSelected);
    } else if (targetSource === 'unselected' && source === 'selected') {
      const newSelected = selectedLocations.filter(item => item !== location);
      onLocationOrderChange(newSelected);
    }

    setDraggedItem(null);
    setDraggedOver(null);
  };

  const handleAddLocation = (location) => {
    const newSelected = [...selectedLocations, location];
    onLocationOrderChange(newSelected);
  };

  const handleRemoveLocation = (location) => {
    const newSelected = selectedLocations.filter(item => item !== location);
    onLocationOrderChange(newSelected);
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newSelected = [...selectedLocations];
      [newSelected[index], newSelected[index - 1]] = [newSelected[index - 1], newSelected[index]];
      onLocationOrderChange(newSelected);
    }
  };

  const handleMoveDown = (index) => {
    if (index < selectedLocations.length - 1) {
      const newSelected = [...selectedLocations];
      [newSelected[index], newSelected[index + 1]] = [newSelected[index + 1], newSelected[index]];
      onLocationOrderChange(newSelected);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Available Locations</h4>
        <div 
          className="min-h-[200px] p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
          onDragOver={handleDragOver}
          onDragEnter={(e) => handleDragEnter(e, 0, 'unselected')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 0, 'unselected')}
        >
          {unselectedLocations.length > 0 ? (
            <div className="space-y-2">
              {unselectedLocations.map((location, index) => (
                <div
                  key={location}
                  draggable
                  onDragStart={(e) => handleDragStart(e, location, 'unselected')}
                  className="flex items-center justify-between p-2 bg-white border rounded cursor-move hover:bg-gray-50"
                >
                  <span className="text-sm">{location}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleAddLocation(location)}
                    className="ml-2"
                  >
                    <i className="bx bx-plus text-xs"></i>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              All locations have been selected
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">
          Selected Order ({selectedLocations.length} locations)
        </h4>
        <div 
          className="min-h-[200px] p-3 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {selectedLocations.length > 0 ? (
            <div className="space-y-2">
              {selectedLocations.map((location, index) => (
                <div
                  key={`${location}-${index}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, location, 'selected')}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, index, 'selected')}
                  onDrop={(e) => handleDrop(e, index, 'selected')}
                  className={`flex items-center justify-between p-2 bg-white border rounded cursor-move hover:bg-gray-50 ${
                    draggedOver?.index === index && draggedOver?.source === 'selected' 
                      ? 'border-blue-500 bg-blue-100' 
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {index + 1}
                    </Badge>
                    <span className="text-sm">{location}</span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="h-6 w-6 p-0"
                    >
                      <i className="bx bx-chevron-up text-xs"></i>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === selectedLocations.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      <i className="bx bx-chevron-down text-xs"></i>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveLocation(location)}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    >
                      <i className="bx bx-x text-xs"></i>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Drag locations from the left to arrange them
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DraggableLocationList; 
